/**
 * "Abertos recentemente": as últimas fichas que a pessoa abriu (processos e clientes).
 *
 * No navegador (localStorage) fica só o tipo, o id e a hora: NUNCA o nome da
 * parte nem dado do processo, porque o sistema guarda processo em segredo de
 * justiça e o computador pode ser compartilhado. O nome é buscado na hora de
 * mostrar, pela API, que confere o acesso de quem está olhando
 * (`useRecentesComNome`, em dadosCasca.js). Se o navegador bloquear o
 * armazenamento, a lista fica vazia e nada quebra.
 */
import { useEffect, useState } from 'react';

const LIMITE = 8;
const EVENTO = 'sj-recentes';
const TIPOS = ['processo', 'cliente'];

function chave(usuarioId) {
  return `sj_recentes_${usuarioId ?? 'anonimo'}`;
}

export function lerRecentes(usuarioId) {
  try {
    const lista = JSON.parse(localStorage.getItem(chave(usuarioId)) || '[]');
    if (!Array.isArray(lista)) return [];
    // Só o formato esperado passa: tipo conhecido, id e hora.
    return lista
      .filter((r) => r && TIPOS.includes(r.tipo) && r.id != null && typeof r.em === 'number')
      .map((r) => ({ tipo: r.tipo, id: r.id, em: r.em }));
  } catch (_) {
    return [];
  }
}

/** Registra uma ficha aberta. A mesma ficha aberta de novo sobe para o topo, sem repetir. */
export function registrarRecente(usuarioId, tipo, id) {
  if (id == null || !TIPOS.includes(tipo)) return;
  try {
    const atual = lerRecentes(usuarioId).filter((r) => !(r.tipo === tipo && String(r.id) === String(id)));
    const novo = [{ tipo, id, em: Date.now() }, ...atual].slice(0, LIMITE);
    localStorage.setItem(chave(usuarioId), JSON.stringify(novo));
    window.dispatchEvent(new CustomEvent(EVENTO));
  } catch (_) {
    // Sem armazenamento: a lista de recentes fica vazia.
  }
}

/** Lista viva: atualiza quando outra parte da tela registra uma ficha. */
export function useRecentes(usuarioId) {
  const [lista, setLista] = useState(() => lerRecentes(usuarioId));
  useEffect(() => {
    setLista(lerRecentes(usuarioId));
    const atualizar = () => setLista(lerRecentes(usuarioId));
    window.addEventListener(EVENTO, atualizar);
    window.addEventListener('storage', atualizar);
    return () => {
      window.removeEventListener(EVENTO, atualizar);
      window.removeEventListener('storage', atualizar);
    };
  }, [usuarioId]);
  return lista;
}

/** "agora", "há 5 min", "há 2 h", "ontem", "12/10". */
export function quandoFoi(ms, agora = Date.now()) {
  const minutos = Math.floor((agora - ms) / 60000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  if (horas < 48) return 'ontem';
  return new Date(ms).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/** "há 12 min", "há 1 h", "ontem, 19:42" ou "30/09, 09:12" (o sino e as notificações). */
export function quandoComHora(iso, agora = Date.now()) {
  const ms = new Date(iso).getTime();
  if (!iso || Number.isNaN(ms)) return '';
  const relativo = quandoFoi(ms, agora);
  const hora = new Date(ms).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (relativo === 'ontem') return `ontem, ${hora}`;
  return /^\d{2}\/\d{2}$/.test(relativo) ? `${relativo}, ${hora}` : relativo;
}

